import {
  ChangeDetectionStrategy,
  Component,
  effect,
  model,
  ModelSignal,
  OnInit,
  Signal,
  signal,
  WritableSignal
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { WooCommerceTipsApiService } from '../../../core/services/woo-commerce/api/woo-commerce-tips-api.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  Subject,
  switchMap,
  tap
} from 'rxjs';
import {
  PostWooCommerceTipBody,
  WooCommerceTipsResponse
} from '../../../core/interfaces/woo-commerce/api/tips/tips-api.interface';
import { select, Store } from '@ngxs/store';
import { CartItemsAction } from '../../state/cart/cart.actions';
import { AmountSelectorComponent } from '../controllers/amount-selector/amount-selector.component';
import {
  WooCommerceCartFee,
  WooCommerceCartTotals
} from '../../../core/interfaces/woo-commerce/api/cart/cart.interface';
import { CartState } from '../../state/cart/cart.state';
import { AmountSelectorType } from '../../interfaces/amount-selector.interface';

@UntilDestroy()
@Component({
  selector: 'app-tip-selector',
  imports: [FormsModule, AmountSelectorComponent],
  templateUrl: './tip-selector.component.html',
  styleUrl: './tip-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TipSelectorComponent implements OnInit {
  isApplyingTip: ModelSignal<boolean> = model<boolean>(false);

  value: WritableSignal<number | null> = signal<number | null>(null);
  amountSelectorType: WritableSignal<AmountSelectorType> =
    signal<AmountSelectorType>(AmountSelectorType.Fixed);

  options: WritableSignal<number[]> = signal<number[]>([]);

  totals: Signal<WooCommerceCartTotals> = select(CartState.totals);
  fees: Signal<WooCommerceCartFee[]> = select(CartState.fees);
  isCartLoading: Signal<boolean> = select(CartState.isLoading('cart'));
  driverFee: Signal<WooCommerceCartFee | undefined> = select(
    CartState.driverFee
  );

  private tipChange$ = new Subject<number>();

  constructor(
    private tipsApiService: WooCommerceTipsApiService,
    private store: Store
  ) {
    effect(() => {
      const fees = this.fees();
      const driverFee = this.driverFee();

      if (this.isCartLoading()) {
        return;
      }

      if (driverFee && fees.length > 0) {
        this.value.set(driverFee.totals.value);
      } else {
        this.value.set(null);
      }
    });
  }

  ngOnInit(): void {
    this.getTipsOptions();
    this.subscribeOnTipChange();
  }

  onTipChange(tip: number) {
    this.tipChange$.next(tip);
  }

  private getTipsOptions(): void {
    this.tipsApiService
      .getTipsOptions()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: ({
          wc_order_tip_rates,
          wc_order_tip_type
        }: WooCommerceTipsResponse) => {
          const numericOptions = wc_order_tip_rates.map((option) =>
            Number(option)
          );

          this.amountSelectorType.set(wc_order_tip_type);
          this.options.set(numericOptions);
        }
      });
  }

  private subscribeOnTipChange(): void {
    this.tipChange$
      .pipe(
        filter((value) => value != null),
        debounceTime(500),
        distinctUntilChanged(),
        tap(() => this.isApplyingTip.set(true)),
        switchMap((tip) => {
          const payload: PostWooCommerceTipBody = {
            tip: tip || 0,
            tip_type: this.amountSelectorType(),
            tip_custom: 1
          };

          const request$ =
            payload.tip === 0
              ? this.tipsApiService.removeTipFromCart()
              : this.tipsApiService.addTipToCart(payload);

          return request$.pipe(finalize(() => this.isApplyingTip.set(false)));
        }),
        untilDestroyed(this)
      )
      .subscribe({
        next: () => {
          this.store.dispatch(new CartItemsAction.Get({ forceUpdate: true }));
        }
      });
  }
}
